import { Alert, Button, Group, List, Stack, Text } from '@mantine/core';
import { Link } from 'react-router';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import type { EmissionReportLogEntry } from '../../types/entities';
import { emissionFieldLabel } from './importableEmissionFields.js';
import { logEntryLabel } from './logEntryLabel.js';

/**
 * Die ZWEI Sorten Hinweise der Vorschau (Sketch 020, Entscheidung 2): Kollision mit vorhandenen
 * Werten, und was der Import nicht lesen konnte. Werte unter der Bestimmungsgrenze, die Marker
 * `n.n.`/`b`, die Sperrliste und Stoffe ohne Portalfeld sind AUSDRUECKLICH keine Hinweiszweige
 * hier -- sie sind Messergebnisse, keine Auffaelligkeit.
 *
 * Seit Phase 40 (Plan 40-08, D-18) faellt die Kollision mit vorhandenen Messungen UND die
 * Kollision mit vorhandenen Massenstrom-Mittelwerten in DIESELBE Kategorie (dieselbe Optik,
 * derselbe Sprung "Zur Meldung") -- keine dritte Hinweiskategorie, nur ein zweiter Alert derselben
 * Art.
 */
export interface PreviewNotesProps {
  /** Lebender Ofenname (D-13), `null` solange nicht bekannt/zugeordnet. */
  kilnName: string | null;
  /** Serverseitig berechnet (Plan 35-07) -- wird angezeigt, nie nachgerechnet. */
  existingMeasurementCount: number | null;
  /** Zahl der Messungen DIESER Vorschau -- wie viele zusaetzlich angelegt werden. */
  addedMeasurementCount: number;
  /** Das Jahr der zugeordneten Meldung, fuer den Kollisionssatz. */
  reportYear: string | null;
  /** Id der zugeordneten Meldung fuer den Sprung "Zur Meldung"; `null` ohne Zuordnung/geloescht. */
  environmentalDataId: number | null;
  /** Protokolleintraege mit Schwere `unread` (`previewStats.unreadEntries`). */
  unreadEntries: EmissionReportLogEntry[];
  /**
   * WR-09 (Punkt 5, Vorschau-Teil): im Protokollmodus (`status !== 'open'`) enthaelt
   * `existingMeasurementCount` bereits die uebernommenen Messungen -- der Kollisionshinweis
   * wuerde dort in der Zukunftsform behaupten, was schon geschehen ist. Er erscheint deshalb NUR
   * in der offenen Vorschau.
   */
  readOnly: boolean;
  /**
   * Phase 40 (Plan 40-05, D-18): serverseitig berechnet (`EmissionReportImport::$existingMassFlowComponents`)
   * -- wird angezeigt, nie nachgerechnet, dieselbe Begruendung wie bei `existingMeasurementCount`
   * (WR-09). Nur in der offenen Vorschau (siehe `readOnly` oben); `null`/leere Liste zeigt keinen
   * Hinweis.
   */
  existingMassFlowComponents?: string[] | null;
}

export function PreviewNotes({
  kilnName,
  existingMeasurementCount,
  addedMeasurementCount,
  reportYear,
  environmentalDataId,
  unreadEntries,
  readOnly,
  existingMassFlowComponents,
}: PreviewNotesProps) {
  const showCollision = !readOnly && (existingMeasurementCount ?? 0) > 0;
  const massFlowCollisionComponents = existingMassFlowComponents ?? [];
  const showMassFlowCollision = !readOnly && massFlowCollisionComponents.length > 0;

  return (
    <Stack gap="sm" mb="md">
      {showCollision && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
            <Text size="sm">
              Ofen {kilnName ?? '—'} hat für {reportYear ?? '—'} bereits {existingMeasurementCount}{' '}
              Messungen. Sie bleiben erhalten — diese Vorschau legt {addedMeasurementCount} weitere
              an, der Ofen hat danach {(existingMeasurementCount ?? 0) + addedMeasurementCount}. Ist
              das ein Neuimport, die vorhandenen zuerst in der Meldung löschen.
            </Text>
            {environmentalDataId !== null && (
              <Button
                component={Link}
                to={`/reports/environmental/${environmentalDataId}`}
                size="xs"
                variant="default"
                style={{ whiteSpace: 'nowrap' }}
              >
                Zur Meldung
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {showMassFlowCollision && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
            <Text size="sm">
              Ofen {kilnName ?? '—'} hat für {reportYear ?? '—'} bereits Mittelwerte der Massenströme
              für {massFlowCollisionComponents.map((key) => emissionFieldLabel(key)).join(', ')}.
              Beim Übernehmen ersetzt dieser Bericht genau diese Werte; die übrigen Komponenten
              bleiben unverändert.
            </Text>
            {environmentalDataId !== null && (
              <Button
                component={Link}
                to={`/reports/environmental/${environmentalDataId}`}
                size="xs"
                variant="default"
                style={{ whiteSpace: 'nowrap' }}
              >
                Zur Meldung
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {unreadEntries.length > 0 ? (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm" fw={600}>
            {unreadEntries.length === 1
              ? 'Eine Stelle konnte nicht gelesen werden.'
              : `${unreadEntries.length} Stellen konnten nicht gelesen werden.`}
          </Text>
          <List size="sm" mt="xs">
            {unreadEntries.map((entry, index) => (
              <List.Item key={index}>
                <Text span fw={600}>
                  {logEntryLabel(entry) ?? '—'}
                </Text>{' '}
                — {entry.message}
              </List.Item>
            ))}
          </List>
        </Alert>
      ) : (
        <Alert color="gray" variant="light" icon={<IconCheck size={16} />}>
          <Text size="sm">
            <Text span fw={600}>
              Der Bericht ist vollständig gelesen.
            </Text>{' '}
            Keine Stelle blieb unverstanden.
          </Text>
        </Alert>
      )}
    </Stack>
  );
}
